import type { ReactNode } from 'react';
import { Alert } from '@ksyos/design-system';
import type {
    IService,
    IServiceOrganizationProvider,
    SelectedServiceOrganizationProviders,
} from './ServiceConsumerAddEdit';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    Button,
    DeleteButton,
    Dropdown,
    FormGroup,
    FormHeader,
    FormLabel,
    InputGroup,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';

interface IServiceOrganizationProviderListAddEditProps {
    onChange: (selectedServiceOrganizationProviders: SelectedServiceOrganizationProviders) => Promise<void>;
    selectedServiceOrganizationProviders: SelectedServiceOrganizationProviders;
    service: IService;
    serviceOrganizationSettingId: string | null;
}

interface IServiceOrganizationProviderListAddEditState {
    selectedServiceOrganizationProviderId: string | null;
    serviceOrganizationProviders: IServiceOrganizationProvider[];
}

export class ServiceOrganizationProviderListAddEdit extends Component<
    IServiceOrganizationProviderListAddEditProps,
    IServiceOrganizationProviderListAddEditState
> {
    constructor(props: IServiceOrganizationProviderListAddEditProps, context?: {}) {
        super(props, context);

        this.state = {
            selectedServiceOrganizationProviderId: null,
            serviceOrganizationProviders: [],
        };
    }

    public async componentDidMount() {
        await this.loadServiceOrganizations(this.props.service.id);
    }

    public async componentDidUpdate(prevProps: Readonly<IServiceOrganizationProviderListAddEditProps>) {
        if (prevProps.service.id !== this.props.service.id) {
            await this.loadServiceOrganizations(this.props.service.id);
        }
    }

    public render() {
        if (!this.props.serviceOrganizationSettingId) {
            return (
                <>
                    <FormHeader>{translateKey('serviceProvider.providers')}</FormHeader>
                    <Alert appearance="warning">
                        Er is geen organisatie instelling voor deze dienst. Je kunt geen leveranciers toevoegen totdat
                        je een organisatie instelling hebt toegevoegd.
                    </Alert>
                </>
            );
        }

        const rows: ReactNode[] = [];

        this.props.selectedServiceOrganizationProviders.valueSeq().forEach((selectedServiceProvider) => {
            if (!selectedServiceProvider) {
                return;
            }
            rows.push(
                <TableRow key={selectedServiceProvider.id}>
                    <TableData>{selectedServiceProvider.name}</TableData>
                    <TableData align="right">
                        <DeleteButton
                            onClick={async () => await this.removeServiceProvider(selectedServiceProvider.id)}
                        />
                    </TableData>
                </TableRow>
            );
        });

        return (
            <>
                <FormHeader>{translateKey('serviceProvider.providers')}</FormHeader>

                {rows.length === 0 ? (
                    <> {translateKey('profile.noProvider', this.props.service.name)} </>
                ) : (
                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('organization.organization')} </TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>{rows}</TableBody>
                    </AdminTable>
                )}

                <FormGroup>
                    <FormLabel>
                        {translateKey('common.add')} {translateKey('organization.organization')}
                    </FormLabel>
                    <InputGroup>
                        <Dropdown
                            onChange={async (value) =>
                                await this.setState({ selectedServiceOrganizationProviderId: value })
                            }
                            placeholder={translateKey('common.selectItem', translateKey('organization.organization'))}
                            selectOptions={this.getOrganizationSelectOptions()}
                            value={this.state.selectedServiceOrganizationProviderId}
                        />
                        <Button
                            color="secondary"
                            disabled={!this.state.selectedServiceOrganizationProviderId}
                            onClick={async () =>
                                await this.addServiceProvider(this.state.selectedServiceOrganizationProviderId)
                            }
                        >
                            {translateKey('common.add')}
                        </Button>
                    </InputGroup>
                </FormGroup>
            </>
        );
    }

    private getOrganizationSelectOptions() {
        let options: { text: string; value: string }[] = [];
        for (const item of this.state.serviceOrganizationProviders) {
            if (!this.props.selectedServiceOrganizationProviders.has(item.id)) {
                options = options.concat({ value: item.id, text: item.name });
            }
        }

        return options;
    }

    private async removeServiceProvider(selectedServiceOrganizationProviderId: string) {
        await this.props.onChange(
            this.props.selectedServiceOrganizationProviders.delete(selectedServiceOrganizationProviderId)
        );
    }

    private async addServiceProvider(selectedServiceOrganizationProviderId: string | null) {
        const selectedServiceOrganizationProvider = await this.state.serviceOrganizationProviders.find(
            (item) => item.id === selectedServiceOrganizationProviderId
        );
        if (selectedServiceOrganizationProviderId && selectedServiceOrganizationProvider) {
            await this.setState({ selectedServiceOrganizationProviderId: null });
            await this.props.onChange(
                this.props.selectedServiceOrganizationProviders.set(
                    selectedServiceOrganizationProviderId,
                    selectedServiceOrganizationProvider
                )
            );
        }
    }

    private async loadServiceOrganizations(serviceId: string) {
        const url = `/api/serviceProvidersList/${serviceId}/organizations`;
        const results = await fetchJson(url, 'GET');
        await this.setState({ serviceOrganizationProviders: results.json.items });
    }
}
